"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface SettingsContextType {
  compactMode: boolean;
  setCompactMode: (value: boolean) => void;
  animations: boolean;
  setAnimations: (value: boolean) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [compactMode, setCompactModeState] = useState(false);
  const [animations, setAnimationsState] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Load from localStorage
    const savedCompact = localStorage.getItem("compactMode");
    const savedAnimations = localStorage.getItem("animations");
    if (savedCompact) setCompactModeState(savedCompact === "true");
    if (savedAnimations !== null) setAnimationsState(savedAnimations !== "false");
  }, []);

  useEffect(() => {
    if (!mounted) return;
    // Apply compact mode class to body
    document.body.classList.toggle("compact-mode", compactMode);
  }, [compactMode, mounted]);

  useEffect(() => {
    if (!mounted) return;
    // Apply animations class to body
    document.body.classList.toggle("no-animations", !animations);
  }, [animations, mounted]);

  const setCompactMode = (value: boolean) => {
    setCompactModeState(value);
    localStorage.setItem("compactMode", String(value));
  };

  const setAnimations = (value: boolean) => {
    setAnimationsState(value);
    localStorage.setItem("animations", String(value));
  };

  return (
    <SettingsContext.Provider value={{ compactMode, setCompactMode, animations, setAnimations }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
